import { Directive, input } from '@angular/core';
import { HlmAlert, HlmAlertAction, HlmAlertDescription, HlmAlertTitle } from '@budgets/ui/helm/alert';
import { classes } from '@budgets/ui/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const alertVariants = cva(
  [
    'group/alert relative grid w-full gap-0.5 rounded-lg border px-6 py-4 text-start text-sm',
    'has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18',
    'has-data-[slot=alert-icon]:grid-cols-[auto_1fr] has-data-[slot=alert-icon]:gap-x-2.5',
  ],
  {
    variants: {
      tone: {
        default: 'bg-card text-card-foreground',
        warning: 'bg-card text-card-foreground',
        destructive: 'bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  },
);

type AlertVariants = VariantProps<typeof alertVariants>;

@Directive({
    selector: '[appUiAlert]',
    hostDirectives: [HlmAlert],
    host: {
        'data-slot': 'alert',
        role: 'alert',
    },
})
export class UiAlertDirective {
  readonly tone = input<AlertVariants['tone']>('default');

  constructor() {
    classes(() => alertVariants({ tone: this.tone() }));
  }
}

@Directive({
    selector: '[appUiAlertTitle]',
    hostDirectives: [HlmAlertTitle],
    host: {
        'data-slot': 'alert-title',
    },
})
export class UiAlertTitleDirective {
  constructor() {
    classes(
      () =>
        'font-medium leading-snug [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
    );
  }
}

@Directive({
    selector: '[appUiAlertDescription]',
    hostDirectives: [HlmAlertDescription],
    host: {
        'data-slot': 'alert-description',
    },
})
export class UiAlertDescriptionDirective {
  constructor() {
    classes(
      () =>
        'text-muted-foreground text-sm text-balance md:text-pretty [&_p:not(:last-child)]:mb-6 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
    );
  }
}

@Directive({
    selector: '[appUiAlertIcon]',
    host: {
        'data-slot': 'alert-icon',
    },
})
export class UiAlertIconDirective {
  constructor() {
    classes(
      () =>
        'row-span-2 flex size-6 translate-y-0.5 shrink-0 items-center justify-center text-current [&_app-ui-icon]:h-full [&_app-ui-icon]:w-full',
    );
  }
}

@Directive({
    selector: '[appUiAlertAction]',
    hostDirectives: [HlmAlertAction],
    host: {
        'data-slot': 'alert-action',
    },
})
export class UiAlertActionDirective {
  constructor() {
    classes(() => 'absolute end-4 top-2.5');
  }
}
